import React, { useEffect, useRef, useState } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonContent,
  IonSpinner,
} from '@ionic/react';
import { close } from 'ionicons/icons';
import Player from '@vimeo/player';
import { AdditionalContent } from '@/types/additionalContent';

interface LearnMoreModalProps {
  isOpen: boolean;
  content: AdditionalContent | null;
  onClose: () => void;
}

export const LearnMoreModal: React.FC<LearnMoreModalProps> = ({
  isOpen,
  content,
  onClose,
}) => {
  const playerRef = useRef<Player | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Validate Vimeo URL
  const isValidVimeoUrl = (url: string): boolean => {
    const patterns = [
      /vimeo\.com\/\d+/,
      /player\.vimeo\.com\/video\/\d+/,
    ];
    return patterns.some(pattern => pattern.test(url));
  };

  // Initialize Vimeo player
  useEffect(() => {
    if (!isOpen || !content?.videoUrl) {
      return;
    }

    const videoUrl = content.videoUrl;

    if (!isValidVimeoUrl(videoUrl)) {
      setError('Invalid Vimeo URL');
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    // Small delay to ensure modal container is rendered
    const timeoutId = setTimeout(() => {
      if (!containerRef.current) {
        setError('Video container not ready');
        setIsLoading(false);
        return;
      }

      setError(null);
      setIsLoading(true);

      // Initialize player with URL to support privacy hash
      // Set autoplay to false and controls to true
      const player = new Player(containerRef.current, {
        url: videoUrl as any,
        width: containerRef.current.offsetWidth,
        responsive: true,
        autoplay: false,
        controls: true,
      });

      playerRef.current = player;

      // Setup player ready handler
      player.ready()
        .then(() => {
          if (isMounted) {
            setIsLoading(false);
          }
        })
        .catch((err) => {
          if (isMounted) {
            setError('Error loading video');
            setIsLoading(false);
          }
        });

      // Handle errors
      player.on('error', () => {
        if (isMounted) {
          setError('Error loading video');
          setIsLoading(false);
        }
      });
    }, 100);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      isMounted = false;

      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (err) {
          // Player cleanup error (safe to ignore)
        }
        playerRef.current = null;
      }
    };
  }, [isOpen, content?.videoUrl]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsLoading(true);
      setError(null);
    }
  }, [isOpen]);

  return (
    <IonModal 
      isOpen={isOpen} 
      onDidDismiss={onClose}
      className="additional-content-modal"
    >
      <IonHeader>
        <IonToolbar>
          <IonTitle>{content?.title || 'Content'}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>
              <IonIcon icon={close} slot="icon-only" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div className="flex flex-col gap-6 max-w-5xl mx-auto py-4">
          {/* Video Player Container */}
          {content?.videoUrl && (
            <div className="w-full max-w-[70%] mx-auto">
              <div className="relative bg-black rounded-lg overflow-hidden" style={{ paddingTop: '56.25%' }}>
                {isLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-10">
                    <IonSpinner name="crescent" color="light" />
                  </div>
                )}

                {error && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-900 text-white z-10">
                    <div className="text-center">
                      <p className="text-lg font-semibold mb-2">⚠️ Error</p>
                      <p className="text-sm">{error}</p>
                    </div>
                  </div>
                )}

                <div
                  ref={containerRef}
                  className="absolute inset-0"
                  style={{ width: '100%', height: '100%' }}
                />
              </div>
            </div>
          )}

          {/* Description */}
          {content?.description && (
            <div className="text-gray-700 text-base leading-relaxed">
              {content.description}
            </div>
          )}
        </div>
      </IonContent>
    </IonModal>
  );
};

